/**
 * The pills' copy of a document (deliverable 15): its props through `docs.get`, its links, and the
 * types and link types, all read through `PillServices`. Live: a `doc.changed` refetches the
 * document only when its `metaVersion` differs from the cached one, so a body save fetches
 * nothing; `links.changed` naming the document refetches its links; `types.changed` the types.
 */
import type { DocTypeV1, DocumentV1, LinkTypeV1, LinkV1 } from '@codex/contracts';
import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { linkTypesKey, typesKey } from '../data/queries.js';
import { type PillServices, usePillServices } from './services.js';

export const docFieldsKey = (docId: string) => ['doc-fields', docId] as const;
export const docLinksKey = (docId: string) => ['doc-links', docId] as const;
export const docQuarantineKey = (docId: string) => ['doc-quarantine', docId] as const;

/** Seeds the pills' copy of the document from a `docs.get` the page already made. */
export function primeDocFields(queryClient: QueryClient, doc: DocumentV1): void {
  queryClient.setQueryData(docFieldsKey(doc.id), doc);
}

const fetchNow = <T>(
  client: QueryClient,
  queryKey: readonly unknown[],
  queryFn: () => Promise<T>,
) =>
  client.fetchQuery({ queryKey, queryFn, staleTime: 0 }).then(
    () => undefined,
    () => undefined,
  );

/** The reads behind each key; the hook and the live watcher share them. */
export function docFieldsReads(services: PillServices, docId: string) {
  return {
    doc: () => services.getDocument(docId),
    links: () => services.listLinks(docId),
    types: () => services.listTypes(),
    linkTypes: () => services.listLinkTypes(),
  };
}

/** Subscribes the three live rules; returns the unsubscribe. */
export function watchDocFields(
  client: QueryClient,
  services: PillServices,
  docId: string,
): () => void {
  const reads = docFieldsReads(services, docId);
  const stops = [
    services.subscribe('doc.changed', (event) => {
      if (event.data.docId !== docId) return;
      const cached = client.getQueryData<DocumentV1>(docFieldsKey(docId));
      if (cached && cached.metaVersion === event.data.metaVersion) return;
      void fetchNow(client, docFieldsKey(docId), reads.doc);
    }),
    services.subscribe('links.changed', (event) => {
      if (!event.data.docIds.includes(docId)) return;
      void fetchNow(client, docLinksKey(docId), reads.links);
    }),
    services.subscribe('types.changed', () => {
      void fetchNow(client, typesKey, reads.types);
    }),
  ];
  return () => {
    for (const stop of stops) stop();
  };
}

export interface DocFields {
  readonly doc: DocumentV1 | undefined;
  readonly links: readonly LinkV1[];
  readonly types: readonly DocTypeV1[];
  readonly linkTypes: readonly LinkTypeV1[];
  readonly loading: boolean;
  readonly error: boolean;
  /** Reads the document again and answers it (undefined when the read failed). */
  readonly refreshDoc: () => Promise<DocumentV1 | undefined>;
  /** Reads the document, its links and the types again. */
  readonly reload: () => void;
}

const STALE_MS = 60_000;

export function useDocFields(docId: string): DocFields {
  const services = usePillServices();
  const client = useQueryClient();
  const reads = docFieldsReads(services, docId);
  const doc = useQuery({
    queryKey: docFieldsKey(docId),
    queryFn: reads.doc,
    staleTime: STALE_MS,
    retry: false,
  });
  const links = useQuery({
    queryKey: docLinksKey(docId),
    queryFn: reads.links,
    staleTime: STALE_MS,
    retry: false,
  });
  const types = useQuery({
    queryKey: typesKey,
    queryFn: reads.types,
    staleTime: STALE_MS,
    retry: false,
  });
  const linkTypes = useQuery({
    queryKey: linkTypesKey,
    queryFn: reads.linkTypes,
    staleTime: STALE_MS,
    retry: false,
  });
  useEffect(() => watchDocFields(client, services, docId), [client, services, docId]);
  const all = [doc, links, types, linkTypes];
  return {
    doc: doc.data,
    links: links.data ?? [],
    types: types.data ?? [],
    linkTypes: linkTypes.data ?? [],
    loading: all.some((q) => q.isPending),
    error: all.some((q) => q.isError),
    refreshDoc: () =>
      client.fetchQuery({ queryKey: docFieldsKey(docId), queryFn: reads.doc, staleTime: 0 }).then(
        (fresh) => fresh,
        () => undefined,
      ),
    reload: () => {
      void fetchNow(client, docFieldsKey(docId), reads.doc);
      void fetchNow(client, docLinksKey(docId), reads.links);
      void fetchNow(client, typesKey, reads.types);
    },
  };
}
